import { computed, watch } from 'vue';
import { useI18n as useVueI18n } from 'vue-i18n';
import { useUserStore } from '@/store/userStore';
import { mapOrganizationLanguageToLocale } from '@/i18n';

/**
 * Composable for internationalization in the dashboard
 * Automatically syncs with organization language settings
 */
export function useI18n() {
  const { t, locale, availableLocales } = useVueI18n();
  const userStore = useUserStore();

  // Watch for organization changes and update locale
  watch(
    () => userStore.currentOrganization?.language,
    (newLanguage) => {
      if (newLanguage) {
        const newLocale = mapOrganizationLanguageToLocale(newLanguage);
        if (newLocale !== locale.value) {
          locale.value = newLocale;
          console.log(
            `[i18n] Locale changed to ${newLocale} based on organization language ${newLanguage}`
          );
        }
      }
    },
    { immediate: true }
  );

  // Computed property for current language display name
  const currentLanguageLabel = computed(() => {
    const languageLabels = {
      'en-EN': 'English',
      'nl-NL': 'Nederlands',
    };
    return languageLabels[locale.value] || 'English';
  });

  // Helper function to set locale manually
  const setLocale = (newLocale) => {
    if (availableLocales.includes(newLocale)) {
      locale.value = newLocale;
    } else {
      console.warn(`[i18n] Locale ${newLocale} is not available`);
    }
  };

  // Helper function to get organization language from locale
  const getOrganizationLanguageFromLocale = (orgLanguage) => {
    // This function maps organization language to i18n locale
    const languageMap = {
      'en-US': 'en-EN',
      'en-EN': 'en-EN',
      'nl-NL': 'nl-NL',
      'nl-BE': 'nl-NL', // Belgian Dutch maps to Dutch
    };
    return languageMap[orgLanguage] || 'en-EN';
  };

  return {
    // Core i18n functions
    t,
    locale: computed(() => locale.value),
    availableLocales,

    // Helper functions
    setLocale,
    currentLanguageLabel,
    getOrganizationLanguageFromLocale,

    // Convenience methods for common translations
    $t: t, // Alias for template usage
  };
}

export default useI18n;
